/**
 * A price series as a shape, not as a table of dates.
 *
 * `get_price_history` and `analyse_performance` both declare `view="series"`,
 * and both rendered as a column of numbers — which is the one presentation a
 * series is worse at than any other. `_series()` in `streaming/emitter.py`
 * already sends aligned points; nothing was missing but the drawing.
 *
 * SCALED TO ITS OWN VALUES, NEVER TO ZERO. NEPSE closes cluster tightly —
 * 512, 514, 516 across a week is an ordinary month — and a zero baseline
 * renders that as a flat line, which is a false statement about volatility
 * made by the axis rather than by the data. The low and the high are labelled
 * so the compression is visible rather than implied.
 *
 * Values stay STRINGS from the wire until the last moment, and are parsed
 * here only to position a point. Nothing parsed here is ever displayed: the
 * caller prints the original string, which is the same rule
 * `figures.ts` states — a Decimal rupee value must not round-trip through a
 * binary float on its way to a reader.
 *
 * EVERY STRING IT HANDS BACK GOES THROUGH `groupText`, which is where that
 * rule and readability meet. A portfolio axis printed `16829497.60` and the
 * product owner it was drawn for said "not clear" — eight undifferentiated
 * digits, in a market whose readers group by lakh and crore. `grouped` is
 * purely lexical: it inserts separators between digits it never reads as a
 * number, so `1,68,29,497.60` is the same figure the engine sent with its
 * magnitude visible. Positioning still parses; printing still does not.
 */

import {
  cellText,
  cellUnit,
  groupText,
  unitPrefix,
  unitSuffix,
  type CellValue,
} from '@/features/mitra/lib/figures'

/**
 * The plot box, in user units. Owned here rather than by a component so
 * the legacy `series` drawing and the catalog's `line` component ask
 * "is there anything to join" of one geometry.
 */
export const PLOT_W = 251
export const PLOT_H = 92

export interface SeriesLine {
  name: string
  /**
   * WHAT THIS LINE IS ABOUT, as the server named it — a symbol, or "" for a
   * measure of the caller's own book.
   *
   * Two measures of one thing belong on one axis: `analyse_performance`
   * sends `value` and `paid_in`, and the whole meaning of that pair is the
   * DISTANCE between them, which two panels on two independent scales cannot
   * show. Two subjects do not: NABIL near 620 beside NICA near 420 on one
   * axis is dominated by the gap between two different securities and both
   * lines flatten into parallel strokes — `web/e2e/chart.spec.ts` argues that
   * out and pins it.
   *
   * `_series()` in streaming/emitter.py is the only party that knows which of
   * those a payload is, because it is the only one holding the rows. The
   * alternative was for a renderer to read it back out of the line NAME, and
   * a tool-to-shape map written in TypeScript is the second copy
   * `destinations.py` says this repo has already paid for twice.
   *
   * OPTIONAL, AND ABSENT MEANS "ITS OWN". A payload recorded before this
   * field existed carries no subject, and the safe reading of an unknown
   * subject is that no two lines share one — which is exactly what this
   * surface drew before subjects existed.
   */
  subject?: string
  /**
   * A POINT'S `y` IS A FIGURE, and this said `string | null` until it was
   * read beside the wire. `_series()` writes `{"x": str(label),
   * "y": _measure(figure)}` — every projection in that module goes through
   * `_measure`, which is what keeps the unit attached — so a point arrives
   * as `{value: "524.50", unit: "NPR", ...}`. `fromBlock` casts a payload
   * straight to this type, so `Number(y)` was NaN on every frame the engine
   * has ever sent: every point dropped as unknown, `plottable` false, and
   * every real series fell through to the table fallback — the one
   * presentation this module exists to replace, on the live wire, with
   * nothing raised. No test saw it, because every fixture in the suite writes
   * `y` as a bare string: the shape the TYPE described rather than the shape
   * the ENGINE sends. `components/Block.tsx` declares `y: ProjectedFigure`
   * and has always drawn it. `CellValue` covers both, because a recording
   * made before `_measure` landed still carries the bare string.
   */
  points: { x: string; y: CellValue }[]
}

/** A value the drawing reaches, ready to be ruled and named. */
export interface Mark {
  /** In the caller's plot coordinates — the same ones the path is in. */
  y: number
  /** The wire's own string, or null when no point carries it. */
  text: string | null
}

/** One drawn point, in plot coordinates, with the strings it stands for. */
export interface PlottedPoint {
  x: number
  y: number
  /** What the wire labelled this point — a date, usually. */
  label: string
  /** The wire's own value string. Never a reparsed number. */
  text: string
}

export interface PlottedLine {
  name: string
  /** An SVG path, or "" when there is nothing to join. */
  d: string
  /**
   * Every point the path passes through, so a caller can put something AT
   * one — a scrub readout, a marker — without re-deriving `y()`.
   *
   * Ruled here for the reason `marks` is: `y()` is this module's private
   * mapping, and a component that recomputes it to place a dot can disagree
   * with the line the dot is supposed to sit on.
   */
  points: PlottedPoint[]
}

export interface Plot {
  plottable: boolean
  lines: PlottedLine[]
  low: number
  high: number
  /** The same two, as the wire wrote them. What a reader is shown. */
  lowText: string | null
  highText: string | null
  /** What the ends say, which is what a reader compares. */
  direction: 'up' | 'down' | 'flat'
  first: string | null
  last: string | null
  /**
   * The values the drawing reaches, in the plot's own coordinates.
   *
   * Ruled here rather than by the caller because `y()` below is this module's
   * private mapping: a component that re-derives it to place a gridline can
   * disagree with the line it is ruling. On a flat series `y()` returns
   * `height / 2` while a naive high/low pair rules the two box edges — two
   * gridlines carrying one figure, neither where the line is.
   */
  marks: Mark[]
  /** Where the series closes, so a mark and the path's end are one place. */
  end: { x: number; y: number } | null
  /**
   * The window's own ends, as the wire labelled them — a date, a fiscal year,
   * whatever `_series()` put in `x`.
   *
   * The drawing had no time on it at all. A month of closes was ruled and
   * named down the value axis and said nothing about WHEN, under a question
   * that had usually named a period out loud ("over the last month"). These
   * are the two labels that make the horizontal direction mean something,
   * and they are the wire's strings: nothing here parses a date.
   */
  firstX: string | null
  lastX: string | null
  /**
   * Where the window OPENED, ruled and named.
   *
   * The drawing said how compressed the window was and where the series
   * ended, and left the reader to infer the only thing the question is
   * usually about: whether it is above or below where it started. The
   * reference draws exactly this line and labels it. It is an honest rule
   * because it names a value the series REACHES — the same bar the first
   * point sits on — which is the standard the high and low marks are held to.
   *
   * Null on a series too short to have gone anywhere, and null when it lands
   * on a mark already drawn, because two rules a pixel apart carrying two
   * labels is worse than one.
   */
  open: Mark | null
  /**
   * The unit every figure on this drawing shares, as affixes, or empty.
   *
   * ONCE, RATHER THAN ON EVERY TICK. A number without its unit is the defect
   * `Figure` exists to prevent, and an axis of eight bare digits was reported
   * as "not clear" by the product owner it was drawn for. On every gridline
   * the currency would be noise repeated three times; on the drawing once it
   * is the one fact the picture cannot supply for itself.
   *
   * EMPTY WHERE THE POINTS DISAGREE. `_fits_series` refuses a two-unit series
   * server-side, so this is normally settled; where it is not, two units on
   * one axis are two scales drawn as one, and labelling that with whichever
   * point iterated first would state something false. No label is the honest
   * answer, and the same one a series of bare strings gets.
   */
  unit: Affixes
}

/** What goes before a figure and what goes after it. See `Plot.unit`. */
export interface Affixes {
  before: string
  after: string
}

const NO_UNIT: Affixes = { before: '', after: '' }

function unitOf(lines: SeriesLine[]): Affixes {
  const carrying = lines
    .flatMap((line) => line.points.map((point) => point.y))
    .filter((cell) => cellUnit(cell) !== '')
  const first = carrying[0]
  if (first === undefined) return NO_UNIT
  const one = cellUnit(first)
  if (carrying.some((cell) => cellUnit(cell) !== one)) return NO_UNIT
  // The affixes come off a real cell rather than off the unit string, so
  // there is one rule for where a currency goes and `figures.ts` holds it.
  return { before: unitPrefix(first), after: unitSuffix(first) }
}

function value(raw: CellValue): number | null {
  // Through `cellText`, for the reason `share()` reads a weight through it:
  // this value crosses the wire two ways, and a reader of one shape drops the
  // other in silence.
  const text = cellText(raw)
  // `Number("")` is 0 and a blank cell is not a close of zero. An honest
  // unknown arrives as cellText's dash and falls out as NaN below.
  if (text === '') return null
  const n = Number(text)
  return Number.isFinite(n) ? n : null
}

export function plot(lines: SeriesLine[], width: number, height: number): Plot {
  const usable = lines.map((line) => ({
    name: line.name,
    // A point with no value is unknown, not zero. Drawn at zero it is a crash
    // that never happened — the same reason the table draws a dash.
    points: line.points
      .map((p) => ({ x: p.x, y: value(p.y) }))
      .filter((p): p is { x: string; y: number } => p.y !== null),
  }))
  const all = usable.flatMap((l) => l.points.map((p) => p.y))
  if (all.length === 0) {
    return {
      plottable: false,
      lines: [],
      low: 0,
      high: 0,
      lowText: null,
      highText: null,
      direction: 'flat',
      first: null,
      last: null,
      marks: [],
      end: null,
      firstX: null,
      lastX: null,
      open: null,
      unit: NO_UNIT,
    }
  }

  const low = Math.min(...all)
  const high = Math.max(...all)
  const span = high - low

  const y = (v: number) =>
    // A flat series has no span to scale against, so it sits on the middle
    // line rather than dividing by zero. Level is a fact worth drawing.
    span === 0 ? height / 2 : height - ((v - low) / span) * height

  const plotted = usable.map((line) => {
    if (line.points.length < 2) return { name: line.name, d: '', points: [] }
    const step = width / (line.points.length - 1)
    const points = line.points.map((p, i) => ({
      x: i * step,
      y: y(p.y),
      label: p.x,
      // The wire's own string for this point, found rather than formatted --
      // the same rule the axis labels are held to. GROUPED, which is not a
      // re-formatting: `grouped` splits the string the engine sent and spaces
      // its digits two-two-three the way this domain counts, parsing nothing,
      // so every digit and the fraction arrive exactly as they were written.
      text: groupText(
        lines.find((l) => l.name === line.name)?.points.find((q) => q.x === p.x)?.y ?? null,
      ),
    }))
    const d = points
      .map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
      .join(' ')
    return { name: line.name, d, points }
  })

  const head = usable[0]?.points ?? []
  const opened = head[0]?.y ?? 0
  const closed = head[head.length - 1]?.y ?? 0
  const direction =
    head.length < 2 || closed === opened
      ? ('flat' as const)
      : closed > opened
        ? ('up' as const)
        : ('down' as const)

  // The strings behind the extremes, found rather than formatted. `low` and
  // `high` are parsed floats and exist only to position a point; printing
  // them put 524.5 on the scale beside 524.50 in the caption — the same value
  // at two precisions, which reads as a bug and is one. "Never invent a
  // precision the source does not have" applies to an axis label as much as
  // to a sentence.
  const everything = lines.flatMap((l) => l.points).filter((p) => value(p.y) !== null)
  // Null when no point carries it, so a mark with no string draws its rule
  // and no label. `cellText(undefined)` would put an em dash on the scale.
  const at = (target: number): string | null => {
    const hit = everything.find((p) => value(p.y) === target)
    return hit === undefined ? null : groupText(hit.y)
  }
  const lowText = at(low)
  const highText = at(high)

  const raw = lines[0]?.points.filter((p) => value(p.y) !== null) ?? []
  const firstRaw = raw[0]
  const lastRaw = raw[raw.length - 1]
  return {
    plottable: plotted.some((l) => l.d !== ''),
    lines: plotted,
    low,
    high,
    lowText,
    highText,
    direction,
    // ONE MARK WHEN THE SERIES IS FLAT: high and low are then one value and
    // `y()` puts it in the MIDDLE of the box, so a caller ruling the top and
    // bottom edges would draw two gridlines carrying one figure, neither of
    // them where the line is.
    marks:
      span === 0
        ? [{ y: y(high), text: highText }]
        : [
            { y: y(high), text: highText },
            { y: y(low), text: lowText },
          ],
    // The strings the wire sent, never the parsed numbers.
    first: firstRaw === undefined ? null : groupText(firstRaw.y),
    last: lastRaw === undefined ? null : groupText(lastRaw.y),
    // `step` puts the last point exactly at `width`, so the mark and the
    // right-hand edge of the drawing are the same place by construction.
    end: head.length < 2 ? null : { x: width, y: y(closed) },
    firstX: firstRaw === undefined ? null : firstRaw.x,
    lastX: lastRaw === undefined ? null : lastRaw.x,
    // Suppressed when the open IS the high or the low: the mark is already
    // ruled and named, and a second rule on the same pixel is noise.
    open:
      head.length < 2 || opened === high || opened === low
        ? null
        : { y: y(opened), text: at(opened) },
    unit: unitOf(lines),
  }
}
